'use client';
// Popup video: either a file uploaded in the panel (styled player) or the Aparat / YouTube embeds.
import React, { useEffect, useRef, useState } from 'react';
import VideoPlayer from '@/components/VideoPlayer';

/** The `video` group of the popup steps in the panel (ACF). */
export interface PopupVideoData {
    /** 'embed' = Aparat / YouTube (default, also for old data), 'upload' = file from the panel. */
    videoSource?: string | null;
    videoFile?: { mediaItemUrl?: string | null; mimeType?: string | null } | null;
    videoPoster?: { sourceUrl?: string | null } | null;
    aparat?: string;
    youtube?: string;
}

const uploadedUrl = (video?: PopupVideoData | null): string =>
    video?.videoSource === 'upload' ? video?.videoFile?.mediaItemUrl || '' : '';

/** True when the group has anything to play. */
export const hasPopupVideo = (video?: PopupVideoData | null): boolean =>
    !!(uploadedUrl(video) || video?.aparat || video?.youtube);

interface PopupVideoProps {
    video?: PopupVideoData | null;
    /** Classes for the Aparat / YouTube player wrapper (kept as each popup had them). */
    embedClassName?: string;
}

const PopupVideo: React.FC<PopupVideoProps> = ({ video, embedClassName }) => {
    const src = uploadedUrl(video);
    if (src) {
        return (
            <PanelVideo
                src={src}
                type={video?.videoFile?.mimeType || undefined}
                poster={video?.videoPoster?.sourceUrl || undefined}
            />
        );
    }
    // Upload chosen but no file yet → still show the embeds if they are filled.
    return (
        <VideoPlayer
            aparatHnembed={video?.aparat}
            youtubeHnembed={video?.youtube}
            classNameCustom={embedClassName}
            fallback
        />
    );
};

interface PanelVideoProps {
    src: string;
    type?: string;
    poster?: string;
}

/**
 * Uploaded video: cover + play button first, the file is only downloaded after the click.
 * Any shape fits the box without cropping: the video is centred and the empty sides show a
 * blurred copy of the same frames (drawn from the playing video, nothing is downloaded twice).
 */
const PanelVideo: React.FC<PanelVideoProps> = ({ src, type, poster }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const backdropRef = useRef<HTMLCanvasElement>(null);
    const [started, setStarted] = useState(false);

    useEffect(() => {
        const video = ref.current;
        const ctx = backdropRef.current?.getContext('2d');
        if (!video || !ctx) return;
        let frame = 0;
        let last = 0;
        const draw = () => {
            if (video.readyState < 2) return;
            try {
                ctx.drawImage(video, 0, 0, ctx.canvas.width, ctx.canvas.height);
            } catch {
                /* frame not ready */
            }
        };
        // ~10 frames a second on a tiny canvas is plenty for a blurred background.
        const loop = (time: number) => {
            if (time - last > 100) {
                draw();
                last = time;
            }
            frame = requestAnimationFrame(loop);
        };
        const start = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(loop);
        };
        const stop = () => {
            cancelAnimationFrame(frame);
            draw();
        };
        video.addEventListener('loadeddata', draw);
        video.addEventListener('seeked', draw);
        video.addEventListener('play', start);
        video.addEventListener('pause', stop);
        video.addEventListener('ended', stop);
        return () => {
            cancelAnimationFrame(frame);
            video.removeEventListener('loadeddata', draw);
            video.removeEventListener('seeked', draw);
            video.removeEventListener('play', start);
            video.removeEventListener('pause', stop);
            video.removeEventListener('ended', stop);
        };
    }, []);

    const play = () => {
        setStarted(true);
        // Play inside the click, so mobile browsers allow sound.
        ref.current?.play().catch(() => undefined);
    };

    return (
        <div className='relative isolate h-full w-full overflow-hidden rounded-[inherit] bg-black'>
            {/* Blurred fill behind the video: the cover first, then the video's own frames. */}
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                {poster && (
                    // eslint-disable-next-line @next/next/no-img-element -- decorative, any panel URL
                    <img
                        src={poster}
                        alt=''
                        className='absolute inset-0 h-full w-full scale-125 object-cover blur-2xl saturate-150'
                    />
                )}
                <canvas
                    ref={backdropRef}
                    width={48}
                    height={27}
                    className='absolute inset-0 h-full w-full scale-125 blur-2xl saturate-150'
                />
                <div className='absolute inset-0 bg-black/20' />
            </div>
            <video
                ref={ref}
                className='relative h-full w-full bg-transparent object-contain'
                controls={started}
                playsInline
                // Without a cover the first frame is the cover, so only the metadata is fetched.
                preload={poster ? 'none' : 'metadata'}
                poster={poster}
                controlsList='nodownload'
            >
                {/* #t=0.1: without a cover, browsers (iOS too) show the first frame instead of black. */}
                <source src={poster ? src : `${src}#t=0.1`} type={type} />
            </video>

            {!started && (
                <button
                    type='button'
                    onClick={play}
                    aria-label='پخش ویدیو'
                    className='group absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/10 to-black/20 transition-colors hover:from-black/70 focus-visible:outline-none'
                >
                    <span className='relative flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-xl ring-8 ring-white/20 backdrop-blur transition-transform duration-200 group-hover:scale-110 group-focus-visible:ring-primary/60 md:h-20 md:w-20'>
                        <span className='absolute inset-0 animate-ping rounded-full bg-white/30 [animation-duration:2s]' />
                        <svg
                            viewBox='0 0 24 24'
                            className='relative ms-1 h-7 w-7 fill-gray-900 md:h-8 md:w-8'
                            aria-hidden='true'
                        >
                            <path d='M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14Z' />
                        </svg>
                    </span>
                </button>
            )}
        </div>
    );
};

export default PopupVideo;
